import { useContext } from "react";
import { Controller } from "react-hook-form";
import { useMember } from "../../context/memberContext";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { SP, postEmergency } from "../../http/serviceportalApi";
import { useTranslations } from "../../hooks/useTranslations";
import { getPayrollOffice } from "../../utils";
import Button from "../ui/Button";
import Checkbox from "../ui/Checkbox";
import CompanySelect from "../ui/CompanySelect";
import Form from "../ui/Form";
import FormField, { Error } from "../ui/FormField";
import Input from "../ui/Input";
import ModalDialog from "../ui/ModalDialog";
import styles from "./EmergencyModal.module.css";

interface FormData {
  companyId?: SP.CompanyId;
  phone: string;
  description: string;
  acceptTerms: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const EmergencyModal = ({ isOpen, onClose }: Props) => {
  const { i18n } = useTranslations();
  const { member } = useMember();
  const dispatch = useContext(NotificationDispatch);

  if (!member) {
    return null;
  }

  return (
    <ModalDialog
      title={i18n.emergencyService.title}
      isOpen={isOpen}
      onClose={onClose}
    >
      <Form<FormData>
        defaultValues={{
          phone: "",
          description: "",
          acceptTerms: false,
        }}
        submitText={i18n.emergencyService.modal.submit}
        onSubmit={() => (formData) => {
          const office = getPayrollOffice(member.giottoId);

          if (!member.giottoId || !office) return;

          const selectedCompany = member.companies?.find(
            (c) => c.id === formData.companyId,
          );
          const firstCompany = member.companies?.[0];

          postEmergency({
            giottoId: member.giottoId,
            companyName:
              // Fall back to member name if the member has no companies
              selectedCompany?.name ?? firstCompany?.name ?? member.name,
            office,
            phone: formData.phone,
            description: formData.description,
          })
            .then(() => {
              dispatch(
                showSuccessNotification(i18n.emergencyService.modal.success),
              );
              onClose();
            })
            .catch((error) =>
              dispatch(
                showErrorNotification(error, i18n.emergencyService.modal.error),
              ),
            );
        }}
        secondaryButton={
          <Button type="secondary" buttonProps={{ onClick: onClose }}>
            {i18n.emergencyService.modal.cancel}
          </Button>
        }
      >
        {({ register, control, formState: { errors } }) => (
          <div className={styles.formFields}>
            {member.companies && member.companies.length > 1 && (
              <FormField
                label={i18n.emergencyService.modal.company}
                direction="column"
                error={errors.companyId}
              >
                <CompanySelect
                  id="company"
                  autoFocus={true}
                  isSingleSelect={true}
                  {...register("companyId", {
                    required: true,
                    valueAsNumber: true,
                  })}
                />
              </FormField>
            )}

            <FormField
              label={i18n.emergencyService.modal.phone}
              direction="column"
              error={errors.phone}
            >
              {({ labelId, isOptional }) => (
                <Input
                  id={labelId}
                  type="tel"
                  {...register("phone", {
                    required: !isOptional,
                  })}
                />
              )}
            </FormField>

            <FormField
              label={i18n.emergencyService.modal.description}
              direction="column"
              error={errors.description}
            >
              {({ labelId, isOptional }) => (
                <textarea
                  id={labelId}
                  rows={7}
                  spellCheck={false}
                  {...register("description", {
                    required: !isOptional,
                  })}
                />
              )}
            </FormField>

            <Controller
              name="acceptTerms"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <>
                  <Checkbox
                    label={i18n.emergencyService.modal.acceptTerms}
                    isSelected={field.value}
                    onChange={field.onChange}
                  />
                  {errors.acceptTerms && (
                    <Error
                      error={{
                        type: "required",
                        message:
                          i18n.emergencyService.modal.acceptTermsRequired,
                      }}
                    />
                  )}
                </>
              )}
            />
          </div>
        )}
      </Form>
    </ModalDialog>
  );
};

export default EmergencyModal;
